import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { jwtDecode } from "jwt-decode";
import CryptoJS from "crypto-js";
import pako from "pako";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState(null);
  const [name, setName] = useState("");
  const [designation, setDesignation] = useState("");
  const [userId, setUserId] = useState(null);

  const [selectedJfmcType, setSelectedJfmcTypeState] =
    useState(null);

  const [jfmcTypeReady, setJfmcTypeReady] = useState(false);

  const JWT_PAYLOAD_SECRET =
    process.env.REACT_APP_JWT_PAYLOAD_SECRET;

  // Restore authentication.
  useEffect(() => {
    const authData = localStorage.getItem("authData");

    if (!authData) return;

    try {
      const parsedData = JSON.parse(authData);
      setAuth(parsedData);

      if (parsedData.token) {
        fetchPermissions(parsedData.token);
      }

      if (parsedData.userToken) {
        const userData = decryptPayload(
          parsedData.userToken,
          JWT_PAYLOAD_SECRET
        );

        if (userData?.user) {
          setUserId(userData.user.id || null);
          setName(userData.user.name || "");
          setDesignation(userData.user.designation || "");
        }
      }
    } catch (error) {
      console.error("Error setting up AuthProvider:", error);
    }
  }, []);

  // Store and restore JFMC type separately for each user.
  useEffect(() => {
    if (!userId) {
      setSelectedJfmcTypeState(null);
      setJfmcTypeReady(false);
      return;
    }

    setJfmcTypeReady(false);

    try {
      const storageKey = `selectedJfmcType_${userId}`;
      const saved = localStorage.getItem(storageKey);

      if (saved) {
        const parsed = JSON.parse(saved);

        // Only accept a complete JFMC type object.
        if (parsed?._id && parsed?.jfmcTypeName) {
          setSelectedJfmcTypeState(parsed);
        } else {
          localStorage.removeItem(storageKey);
          setSelectedJfmcTypeState(null);
        }
      } else {
        setSelectedJfmcTypeState(null);
      }
    } catch (error) {
      console.error("Error restoring JFMC type:", error);
      setSelectedJfmcTypeState(null);
    } finally {
      setJfmcTypeReady(true);
    }
  }, [userId]);

  const setSelectedJfmcType = useCallback(
    (type) => {
      setSelectedJfmcTypeState(type || null);

      if (!userId) return;

      const storageKey = `selectedJfmcType_${userId}`;

      if (type?._id && type?.jfmcTypeName) {
        localStorage.setItem(storageKey, JSON.stringify(type));
      } else {
        localStorage.removeItem(storageKey);
      }
    },
    [userId]
  );

  const resetPermissions = () => {
    setPermissions([]);
  };

  const fetchPermissions = (token) => {
    try {
      const decryptedData = decryptPayload(
        token,
        JWT_PAYLOAD_SECRET
      );

      if (decryptedData?.user) {
        const user = decryptedData.user;

        setPermissions(user.permissions || []);
        setRole(user.userRole || null);
        setName(user.name || "");
        setDesignation(user.designation || "");
        setUserId(user.id || null);
      } else {
        setPermissions([]);
        setRole(null);
        setName("");
        setDesignation("");
        setUserId(null);
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  };

  const decryptPayload = (token, secretKey) => {
    try {
      if (!token || !secretKey) return null;

      const decoded = jwtDecode(token);

      if (!decoded?.iv || !decoded?.encryptedPayload) {
        console.error("Invalid token format.");
        return null;
      }

      const encryptedWordArray = CryptoJS.enc.Hex.parse(
        decoded.encryptedPayload
      );
      const ivWordArray = CryptoJS.enc.Hex.parse(decoded.iv);

      const cipherParams = CryptoJS.lib.CipherParams.create({
        ciphertext: encryptedWordArray,
      });

      const decrypted = CryptoJS.AES.decrypt(
        cipherParams,
        CryptoJS.enc.Hex.parse(secretKey),
        {
          iv: ivWordArray,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        }
      );

      let decryptedText = decrypted.toString(CryptoJS.enc.Utf8);

      if (!decryptedText) return null;

      // Support compressed payloads and plain JSON payloads.
      try {
        const binary = atob(decryptedText);
        const bytes = Uint8Array.from(
          binary,
          (character) => character.charCodeAt(0)
        );

        decryptedText = pako.inflate(bytes, { to: "string" });
      } catch {
        // Payload may already be plain JSON.
      }

      return JSON.parse(decryptedText);
    } catch (error) {
      console.error("Decryption error:", error);
      return null;
    }
  };

  const clearAuthData = () => {
    localStorage.removeItem("authData");
    sessionStorage.removeItem("authData");

    setAuth(null);
    setPermissions([]);
    setRole(null);
    setName("");
    setDesignation("");
    setUserId(null);
    setSelectedJfmcTypeState(null);
    setJfmcTypeReady(false);
  };

  return (
    <AuthContext.Provider
      value={{
        auth,
        setAuth,
        permissions,
        setPermissions,
        role,
        resetPermissions,
        clearAuth: clearAuthData,
        fetchPermissions,
        name,
        designation,
        userId,
        selectedJfmcType,
        setSelectedJfmcType,
        jfmcTypeReady,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};